import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import InputField from "./InputField";
import { RiDeleteBin6Line } from "react-icons/ri";

const PostWiseDetailEntry = ({
  entry,
  outerIndex,
  innerIndex,
  handleInputChange,
  deleteFormEntry,
  fieldErrors,
  style,
  checkErrorFn,
  parentStartDate,
  parentEndDate,
  setFieldErrors,
  calculateTotalExperience,
  validateExperienceConsistency,
  postWiseTotalWithoutTraineeFun,
  allEntries,
  minExperienceDate,
  applicationDetails,
}) => {
  // Local state to store date range error message
  const [dateRangeError, setDateRangeError] = useState("");

  /**
   * Validates if the 'To' date is not earlier than the 'From' date
   * and whether dates fall within parentStartDate..parentEndDate.
   * Also validates against minimum experience date (highest qualification passing year).
   * Sets an error message if invalid.
   */
  const validateDateRange = () => {
    const { startDateOfService, endDateOfService } = entry;

    if (!startDateOfService || !endDateOfService) {
      setDateRangeError("Both From and To dates are required.");
      return false;
    }

    const start = new Date(startDateOfService);
    const end = new Date(endDateOfService);

    if (start > end) {
      setDateRangeError(
        "The 'To' date cannot be earlier than the 'From' date."
      );
      return false;
    }

    // Validate against minimum experience date (highest qualification passing year)
    if (minExperienceDate) {
      const minDate = new Date(minExperienceDate);
      if (start < minDate) {
        const highestPassingYear = minDate.getFullYear();
        setDateRangeError(
          `Start date cannot be earlier than your highest qualification passing year (${highestPassingYear}). Please enter a date from ${highestPassingYear}-01-01 onwards.`
        );
        return false;
      }
    }

    // Ensure within parent's range if parent dates provided
    if (parentStartDate) {
      const parentStart = new Date(parentStartDate);
      if (start < parentStart) {
        setDateRangeError(`Start date must be on or after ${parentStartDate}.`);
        return false;
      }
    }
    if (parentEndDate) {
      const parentEnd = new Date(parentEndDate);
      if (end > parentEnd) {
        setDateRangeError(`End date must be on or before ${parentEndDate}.`);
        return false;
      }
    }

    setDateRangeError(""); // Clear error if valid
    return true;
  };

  function getMinStartDateForEntry(entries, currentIndex, parentStartDate) {
    if (currentIndex === 0) return parentStartDate || minExperienceDate;
    const prevEndDate = entries?.[currentIndex - 1]?.endDateOfService;
    if (prevEndDate) {
      // Next day after previous end date
      const nextDay = new Date(prevEndDate);
      nextDay.setDate(nextDay.getDate() + 1);
      return nextDay.toISOString().split("T")[0];
    }
    return parentStartDate || minExperienceDate;
  }

  /**
   * useEffect to validate date range whenever the start or end dates change.
   * Updates both local and global error states.
   */
  useEffect(() => {
    const isValid = validateDateRange();

    if (!isValid) {
      // Set global field error
      setFieldErrors((prevState) => ({
        ...prevState,
        [`dateRangeError${outerIndex}${innerIndex}`]: {
          errors: [
            dateRangeError ||
              "The 'To' date cannot be earlier than the 'From' date.",
          ],
          touched: true,
        },
      }));
    } else {
      // Clear global field error
      setFieldErrors((prevState) => {
        const { [`dateRangeError${outerIndex}${innerIndex}`]: _, ...rest } =
          prevState;
        return rest;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entry.startDateOfService,
    entry.endDateOfService,
    parentStartDate,
    parentEndDate,
    minExperienceDate,
    dateRangeError,
  ]);

  /**
   * useEffect to calculate total experience and validate consistency.
   * This ensures that state updates are handled after validations.
   */
  useEffect(() => {
    const totalExperience = calculateTotalExperience(
      entry.startDateOfService,
      entry.endDateOfService,
      false
    );

    handleInputChange(
      outerIndex,
      innerIndex,
      "totalExperience",
      totalExperience
    );

    // Only proceed if date range is valid
    if (
      entry.startDateOfService &&
      entry.endDateOfService &&
      validateDateRange()
    ) {
      validateExperienceConsistency();
      postWiseTotalWithoutTraineeFun();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.startDateOfService, entry.endDateOfService]);

  /**
   * Auto-clamp child entry dates when parentStartDate/parentEndDate or sibling entries change.
   * Ensures child's start/end lie within computed allowed range.
   */
  useEffect(() => {
    const computedMinStart = getMinStartDateForEntry(
      allEntries,
      innerIndex,
      parentStartDate
    );
    const computedMaxEnd =
      parentEndDate || new Date().toISOString().split("T")[0];

    let newStart = entry.startDateOfService;
    let newEnd = entry.endDateOfService;
    let changed = false;

    if (newStart) {
      if (computedMinStart && new Date(newStart) < new Date(computedMinStart)) {
        newStart = computedMinStart;
        changed = true;
      }
      if (computedMaxEnd && new Date(newStart) > new Date(computedMaxEnd)) {
        newStart = computedMaxEnd;
        changed = true;
      }
    }
    if (newEnd) {
      // ensure end >= start
      if (newStart && new Date(newEnd) < new Date(newStart)) {
        newEnd = newStart;
        changed = true;
      }
      if (computedMaxEnd && new Date(newEnd) > new Date(computedMaxEnd)) {
        newEnd = computedMaxEnd;
        changed = true;
      }
      if (computedMinStart && new Date(newEnd) < new Date(computedMinStart)) {
        newEnd = computedMinStart;
        changed = true;
      }
    }

    if (changed) {
      // push updated dates back to parent formData
      handleInputChange(
        outerIndex,
        innerIndex,
        "startDateOfService",
        newStart || ""
      );
      handleInputChange(
        outerIndex,
        innerIndex,
        "endDateOfService",
        newEnd || ""
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentStartDate, parentEndDate, allEntries]);

  /**
   * Helper function to perform local validation on other fields.
   */
  const localCheckErrorFn = (fieldKey, immediateValue) => {
    let errors = [];
    if (!immediateValue) {
      errors.push("This field is required.");
    } else if (
      (fieldKey.includes("startDateOfService") ||
        fieldKey.includes("endDateOfService")) &&
      !isDateWithinRange(immediateValue, parentStartDate, parentEndDate)
    ) {
      errors.push(
        `Date must be between ${parentStartDate || minExperienceDate} and ${
          parentEndDate || new Date().toISOString().split("T")[0]
        }.`
      );
    }

    setFieldErrors((prevState) => ({
      ...prevState,
      [fieldKey]: {
        errors: errors,
        touched: true,
      },
    }));
    return errors.length === 0;
  };

  /**
   * Helper function to check if a date is within a given range.
   */
  const isDateWithinRange = (date, minDate, maxDate) => {
    if (!date) return false;
    const dateValue = new Date(date);
    const min = minDate
      ? new Date(minDate)
      : new Date(minExperienceDate || "1900-01-01");
    const max = maxDate ? new Date(maxDate) : new Date();
    return dateValue >= min && dateValue <= max;
  };

  const computedMinStart = getMinStartDateForEntry(
    allEntries,
    innerIndex,
    parentStartDate
  );

  const isLegalDept =
    applicationDetails?.vacancyId?.department?.name?.trim()?.toLowerCase() ===
    "legal";
  const employmentCategoryOtherLabel = isLegalDept
    ? "Other ( Regular / Contractual / Probationer / Practitioner )"
    : "Other ( Regular / Contractual / Probationer )";

  return (
    <React.Fragment className="mb-3">
      <hr className="mt-2" />
      <Row>
        <Col xs={12} md={4}>
          <InputField
            label="Employment Category:"
            type="select"
            value={entry.isTrainee}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(outerIndex, innerIndex, "isTrainee", value);
              checkErrorFn(`isTrainee${outerIndex}${innerIndex}`, value);
            }}
            onBlur={() =>
              checkErrorFn(
                `isTrainee${outerIndex}${innerIndex}`,
                entry.isTrainee
              )
            }
            error={fieldErrors?.[`isTrainee${outerIndex}${innerIndex}`]?.errors}
            style={style}
            required
            options={[
              { value: "Yes", label: "Trainee" },
              {
                value: "No",
                label: employmentCategoryOtherLabel,
              },
            ]}
          />
        </Col>
        <Col xs={12} md={8}>
          <InputField
            label="Nature of Job Handled:"
            value={entry.natureOfJobHandled}
            max={500}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(
                outerIndex,
                innerIndex,
                "natureOfJobHandled",
                value
              );
              checkErrorFn(
                `natureOfJobHandled${outerIndex}${innerIndex}`,
                value
              );
            }}
            onBlur={() =>
              checkErrorFn(
                `natureOfJobHandled${outerIndex}${innerIndex}`,
                entry.natureOfJobHandled
              )
            }
            error={
              fieldErrors?.[`natureOfJobHandled${outerIndex}${innerIndex}`]
                ?.errors
            }
            style={style}
            required
          />
        </Col>
      </Row>

      <Row className="mt-2">
        <Col xs={12} md={8}>
          <InputField
            label="Designation:"
            value={entry.designation}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(outerIndex, innerIndex, "designation", value);
              checkErrorFn(`designation${outerIndex}${innerIndex}`, value);
            }}
            onBlur={() =>
              checkErrorFn(
                `designation${outerIndex}${innerIndex}`,
                entry.designation
              )
            }
            error={
              fieldErrors?.[`designation${outerIndex}${innerIndex}`]?.errors
            }
            style={style}
            required
          />
        </Col>
      </Row>
      <Row className="mt-2">
        <Col xs={12} md={4}>
          <InputField
            label="From:"
            type="date"
            value={entry.startDateOfService}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(
                outerIndex,
                innerIndex,
                "startDateOfService",
                value
              );
            }}
            onBlur={() => {
              localCheckErrorFn(
                `startDateOfService${outerIndex}${innerIndex}`,
                entry.startDateOfService
              );
              checkErrorFn(
                `startDateOfService${outerIndex}${innerIndex}`,
                entry.startDateOfService
              );
            }}
            error={
              fieldErrors?.[`startDateOfService${outerIndex}${innerIndex}`]
                ?.errors
            }
            style={style}
            required
            min={computedMinStart}
            max={parentEndDate || new Date().toISOString().split("T")[0]}
          />
        </Col>
        <Col xs={12} md={4}>
          <InputField
            label="To:"
            type="date"
            value={entry.endDateOfService}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(
                outerIndex,
                innerIndex,
                "endDateOfService",
                value
              );
            }}
            onBlur={() => {
              localCheckErrorFn(
                `endDateOfService${outerIndex}${innerIndex}`,
                entry.endDateOfService
              );
              checkErrorFn(
                `endDateOfService${outerIndex}${innerIndex}`,
                entry.endDateOfService
              );
            }}
            error={
              fieldErrors?.[`endDateOfService${outerIndex}${innerIndex}`]
                ?.errors
            }
            style={style}
            required
            min={entry.startDateOfService || computedMinStart}
            max={parentEndDate || new Date().toISOString().split("T")[0]}
          />
          {/* Display Date Range Error */}
          {dateRangeError && (
            <div className="text-danger mt-2">{dateRangeError}</div>
          )}
        </Col>

        {/* Total Experience */}
        <Col xs={12} md={4}>
          <InputField
            label="Total Experience:"
            value={entry.totalExperience}
            onChange={() => {
              // This field is read-only; no action needed
            }}
            error={
              fieldErrors?.[`totalExperience${outerIndex}${innerIndex}`]?.errors
            }
            style={style}
            required
            readOnly={true}
          />
        </Col>
        <Col xs={12} md={12} className="d-flex align-items-end justify-content-end mt-3 mb-2">
          <Button
            variant="danger"
            size="lg"
            onClick={() => deleteFormEntry(outerIndex, innerIndex)}
            style={{ backgroundColor: "#dc3545", borderColor: "#dc3545" }}
          >
            <RiDeleteBin6Line />
          </Button>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default PostWiseDetailEntry;
